import React, { useState } from "react";
import {
  Compass,
  Heart,
  MessageCircle,
  Sun,
  User,
  Crown,
  ShieldCheck,
  Search,
  Send,
  Gift,
  CheckCheck,
  Sparkles,
} from "lucide-react";

const serif = { fontFamily: "'Playfair Display', serif" };
const sans = { fontFamily: "'Manrope', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

const NAVY = "#0A2A5A";
const GOLD = "#F9AB0D";
const CREAM = "#F9F6F2";

const conversations = [
  { id: 1, name: "Sarah M.", img: 47, last: "That meeting speaker was incredible 🙌", time: "2m", unread: 2, online: true, clean: "2 yrs" },
  { id: 2, name: "Jessica R.", img: 44, last: "Coffee Saturday sounds perfect", time: "1h", unread: 0, online: true, clean: "14 mo" },
  { id: 3, name: "Amanda K.", img: 26, last: "You: What step are you working?", time: "3h", unread: 0, online: false, clean: "3 yrs" },
  { id: 4, name: "Rachel T.", img: 31, last: "Congrats on your anniversary!! 🎉", time: "1d", unread: 0, online: false, clean: "8 mo" },
  { id: 5, name: "Megan L.", img: 16, last: "You: Same! Hiking is my therapy", time: "2d", unread: 0, online: false, clean: "5 yrs" },
];

const messages = [
  { own: false, text: "Hey! I saw you're also working the steps in AA. How long have you been in the program?", time: "10:42 AM" },
  { own: true, text: "Two years next month! Honestly the best decision I ever made. You?", time: "10:45 AM", read: true },
  { own: false, text: "Coming up on 2 years too! We should celebrate our anniversaries at that new sober café downtown ☕", time: "10:47 AM" },
  { own: true, text: "That meeting speaker was incredible 🙌 And yes — it's a date!", time: "10:51 AM", read: true },
];

const navItems = [
  { icon: Compass, label: "Discover" },
  { icon: Heart, label: "Matches" },
  { icon: MessageCircle, label: "Messages", active: true, badge: 2 },
  { icon: Sun, label: "My Journey" },
  { icon: User, label: "Profile" },
];

export default function RedesignPreviewApp() {
  const [selected, setSelected] = useState(1);
  const active = conversations.find((c) => c.id === selected);

  return (
    <div style={{ ...sans, backgroundColor: CREAM }} className="h-screen flex overflow-hidden text-slate-700">
      {/* PANEL 1 — Navy Sidebar */}
      <aside className="w-64 flex-shrink-0 flex flex-col" style={{ backgroundColor: NAVY }} data-testid="preview-sidebar">
        <div className="px-7 pt-8 pb-10 flex items-center gap-3">
          <img src="/images/logo-icon.png" alt="RecoveryMatch" className="w-9 h-9 rounded-lg" data-testid="preview-sidebar-logo" />
          <p className="text-xl font-bold" style={serif}>
            <span className="text-white">Recovery</span>
            <span style={{ color: GOLD }}>Match</span>
          </p>
        </div>
        <nav className="flex-1 px-4 space-y-1">
          {navItems.map(({ icon: Icon, label, active: isActive, badge }) => (
            <div
              key={label}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-colors"
              style={
                isActive
                  ? { backgroundColor: "rgba(249,171,13,0.15)", color: GOLD }
                  : { color: "rgba(255,255,255,0.65)" }
              }
              data-testid={`preview-nav-${label.toLowerCase().replace(" ", "-")}`}
            >
              <Icon size={20} />
              <span className={`text-sm ${isActive ? "font-bold" : "font-medium"}`}>{label}</span>
              {badge && (
                <span
                  className="ml-auto text-[10px] font-extrabold rounded-full w-5 h-5 flex items-center justify-center"
                  style={{ backgroundColor: GOLD, color: NAVY }}
                >
                  {badge}
                </span>
              )}
            </div>
          ))}
        </nav>
        {/* Recovery+ upsell */}
        <div className="m-4 rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(249,171,13,0.3)" }}>
          <Crown size={20} style={{ color: GOLD }} className="mb-2" />
          <p className="text-white text-sm font-bold mb-1">Recovery+</p>
          <p className="text-xs mb-3" style={{ color: "rgba(255,255,255,0.6)" }}>
            Unlimited messages & see who likes you
          </p>
          <button
            className="w-full rounded-full py-2 text-xs font-extrabold transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: GOLD, color: NAVY }}
            data-testid="preview-upgrade-btn"
          >
            Upgrade
          </button>
        </div>
        {/* User chip */}
        <div className="px-6 py-5 flex items-center gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <img src="https://i.pravatar.cc/64?img=59" alt="" className="w-9 h-9 rounded-full border-2" style={{ borderColor: GOLD }} />
          <div>
            <p className="text-white text-sm font-bold leading-none">Michael, 34</p>
            <p className="text-[10px] mt-1 font-bold uppercase tracking-wider" style={{ ...mono, color: GOLD }}>
              2 yrs clean
            </p>
          </div>
        </div>
      </aside>

      {/* PANEL 2 — Conversation list */}
      <section
        className="w-[380px] flex-shrink-0 flex flex-col"
        style={{ borderRight: "1px solid rgba(10,42,90,0.08)" }}
        data-testid="preview-conversation-list"
      >
        <div className="px-6 pt-8 pb-4">
          <h1 className="text-3xl mb-5" style={{ ...serif, color: NAVY }}>
            Messages
          </h1>
          <div
            className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3"
            style={{ border: "1px solid rgba(10,42,90,0.12)" }}
          >
            <Search size={16} className="text-slate-400" />
            <span className="text-sm text-slate-400">Search conversations...</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
          {conversations.map((c) => (
            <div
              key={c.id}
              onClick={() => setSelected(c.id)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-2xl cursor-pointer transition-colors"
              style={selected === c.id ? { backgroundColor: "#FFFFFF", boxShadow: "0 1px 4px rgba(10,42,90,0.08)" } : {}}
              data-testid={`preview-conversation-${c.id}`}
            >
              <div className="relative flex-shrink-0">
                <img src={`https://i.pravatar.cc/96?img=${c.img}`} alt="" className="w-12 h-12 rounded-full object-cover" />
                {c.online && (
                  <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: "#22C55E" }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="font-bold text-sm" style={{ color: NAVY }}>
                    {c.name}
                  </p>
                  <span className="text-[11px] text-slate-400">{c.time}</span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className={`text-xs truncate ${c.unread ? "font-bold text-slate-800" : "text-slate-500"}`}>{c.last}</p>
                  {c.unread > 0 && (
                    <span
                      className="ml-2 flex-shrink-0 text-[10px] font-extrabold rounded-full w-5 h-5 flex items-center justify-center"
                      style={{ backgroundColor: NAVY, color: "#fff" }}
                    >
                      {c.unread}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PANEL 3 — Active chat */}
      <main className="flex-1 flex flex-col bg-white" data-testid="preview-chat-panel">
        {/* Chat header */}
        <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(10,42,90,0.08)" }}>
          <div className="flex items-center gap-4">
            <img src={`https://i.pravatar.cc/96?img=${active.img}`} alt="" className="w-11 h-11 rounded-full" />
            <div>
              <p className="font-bold" style={{ color: NAVY }}>
                {active.name}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ ...mono, color: GOLD }}>
                {active.clean} clean • AA • Online
              </p>
            </div>
          </div>
          <div
            className="flex items-center gap-2 rounded-full px-4 py-2"
            style={{ backgroundColor: "rgba(249,171,13,0.12)" }}
          >
            <ShieldCheck size={15} style={{ color: GOLD }} />
            <span className="text-xs font-bold" style={{ color: NAVY }}>
              Safety First — We've got your back
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-5" style={{ backgroundColor: "#FDFCFA" }}>
          <div className="flex justify-center">
            <span
              className="text-[10px] font-bold uppercase tracking-widest rounded-full px-4 py-1.5"
              style={{ ...mono, backgroundColor: "rgba(10,42,90,0.06)", color: "rgba(10,42,90,0.5)" }}
            >
              Today
            </span>
          </div>
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.own ? "justify-end" : "justify-start"}`}>
              <div className="max-w-md">
                <div
                  className={`px-5 py-3.5 text-sm leading-relaxed ${m.own ? "rounded-3xl rounded-br-lg text-white" : "rounded-3xl rounded-bl-lg"}`}
                  style={
                    m.own
                      ? { backgroundColor: NAVY }
                      : { backgroundColor: "#F1EDE6", color: "#1e293b" }
                  }
                >
                  {m.text}
                </div>
                <div className={`flex items-center gap-1 mt-1.5 ${m.own ? "justify-end" : ""}`}>
                  <span className="text-[10px] text-slate-400">{m.time}</span>
                  {m.own && m.read && <CheckCheck size={13} style={{ color: GOLD }} />}
                </div>
              </div>
            </div>
          ))}
          {/* Guided prompt chip */}
          <div className="flex justify-center pt-2">
            <div
              className="flex items-center gap-2 rounded-full px-5 py-2.5 cursor-pointer transition-transform hover:scale-[1.02]"
              style={{ border: "1.5px dashed rgba(249,171,13,0.6)", backgroundColor: "rgba(249,171,13,0.06)" }}
              data-testid="preview-guided-prompt"
            >
              <Sparkles size={14} style={{ color: GOLD }} />
              <span className="text-xs font-bold" style={{ color: NAVY }}>
                Guided prompt: "What does a perfect clean and sober Saturday look like?"
              </span>
            </div>
          </div>
        </div>

        {/* Composer */}
        <div className="px-8 py-5" style={{ borderTop: "1px solid rgba(10,42,90,0.08)" }}>
          <div
            className="flex items-center gap-3 rounded-full px-5 py-2"
            style={{ backgroundColor: CREAM, border: "1px solid rgba(10,42,90,0.12)" }}
          >
            <Gift size={20} className="cursor-pointer transition-colors" style={{ color: GOLD }} data-testid="preview-gift-btn" />
            <input
              className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-slate-400"
              placeholder="Write a message..."
              data-testid="preview-message-input"
            />
            <button
              className="rounded-full w-10 h-10 flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
              style={{ backgroundColor: GOLD }}
              data-testid="preview-send-btn"
            >
              <Send size={16} style={{ color: NAVY }} />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
